<script lang="typescript">
    // imports
    import { scaleTime } from "d3-scale";
    import { timeHour } from "d3-time";
    import { timeFormat, timeParse } from "d3-time-format";
    import { extent } from "d3-array";
    import { onMount } from "svelte";
    import type { ExternalBar, Marks } from "./timeline";

    export let bars: ExternalBar[] = [];
    let eBars: ExternalBar[] = [];
    let iBars: InternalBar[] = [];

    export let marks: Marks[] = [];
    let eMarks: Marks[] = [];
    let iMarks: IMark[] = [];

    interface Axy {
        label: string;
        x: number;
        width: number;
    }

    $: {
        eBars = bars;
        eMarks = marks;
        console.log({ bars });

        render();
    }

    let formatSmallDate = timeFormat("%Y-%m-%d");
    let formatHour = timeFormat("%H:%M");
    let formatHourNoPadding = timeFormat("%-H:%-M");

    let parseFullDate = timeParse("%Y-%m-%d %H:%M:%S");
    let processing = false;

    // layout
    let width = 1000;
    let padding = 14;

    let axys: Axy[] = [];

    // API

    const render = async () => {
        processing = true;
        // let parseDate = formatDate.parse;
        let nowDate = new Date();
        let nowString = formatSmallDate(nowDate);

        let barDates = eBars
            .map((eBar) => ({
                name: eBar.name,
                kind: eBar.kind,
                color: eBar.color,
                background: eBar.background,
                startString: `${nowString} ${eBar.start}`,
                endString: `${nowString} ${eBar.end}`,
            }))
            .map((sBar) => ({
                name: sBar.name,
                kind: sBar.kind,
                color: sBar.color,
                background: sBar.background,
                startDate: parseFullDate(sBar.startString),
                endDate: parseFullDate(sBar.endString),
            }));

        let markDates = eMarks.map((e) =>
            parseFullDate(`${nowString} ${e.time}`)
        );

        let barDatesArray = barDates.reduce(
            (arr, toReduce) =>
                arr.concat([toReduce.startDate, toReduce.endDate]),
            []
        ) as Date[];

        const [minDate, maxDate] = extent([...barDatesArray, ...markDates]) as [
            Date,
            Date
        ];

        // after get the min and max dates.. and subtract 1h from the min and
        // add 1h to the max... just to create a better sense of time/space
        // is importante do do not use the min and max , only "clone" the old values...
        const minDateMinus = new Date(minDate.getTime());
        minDateMinus.setHours(minDate.getHours() - 1);

        const maxDatePlus = new Date(maxDate.getTime());
        maxDatePlus.setHours(maxDate.getHours() + 1);

        const xTimeScale = scaleTime()
            .domain([minDateMinus, maxDatePlus])
            .nice()
            .clamp(true)
            .range([0 + padding, width]);

        const xHourSource = xTimeScale.ticks(timeHour);

        // createa all  X axy ticks
        let iAxys = [];
        xHourSource.forEach((d: Date, index: number) => {
            iAxys.push({
                x: xTimeScale(d) as number,
                label: formatHour(d),
                width: width / (xHourSource.length || 1),
            });
        });
        axys = iAxys;

        // create array bars
        let newBars: InternalBar[] = [];

        barDates.map((bar) => {
            newBars.push({
                label: `${bar.name} - (${formatHour(
                    bar.startDate
                )} - ${formatHour(bar.endDate)})`,
                kind: bar.kind,
                color: bar.color,
                background: bar.background,
                x: xTimeScale(bar.startDate),
                width: xTimeScale(bar.endDate) - xTimeScale(bar.startDate),
                startDate: bar.startDate,
                endDate: bar.endDate,
            });
        });

        // translate all marks to internal Marks
        iMarks = eMarks.map((m) => {
            let date = parseFullDate(`${nowString} ${m.time}`);
            let x = xTimeScale(date);

            let iMark: IMark = {
                name: `${formatHour(date)}  ${m.name}`,
                date,
                color: m.color,
                x,
            };

            return iMark;
        });

        iBars = newBars;
        processing = false;
    };

    onMount(() => {});
</script>

<div class="timeline">
    <div class="axys">
        {#each axys as axy}
            <span
                class="axy"
                style={`
          left: ${axy.x - 14}px;
          width: ${axy.width}px;
        `}>{axy.label}</span
            >
        {/each}
    </div>

    <div class="axy-bars">
        {#each axys as axy}
            <div
                class="axy-bar"
                style={`
          left: ${axy.x}px;
        `}
            >
                <span />
            </div>
        {/each}
    </div>

    <!-- Bars -->

    {#each iBars as iBar, barIndex}
        <span
            class="label"
            style={` 
                left: ${iBar.x}px;
                width: ${Math.max(iBar.width, 500)}px;
                color: ${iBar.color};
                margin-top: ${32 + (barIndex + 1) * 86}px; `}>{iBar.label}</span
        >
        <span
            class="label kind"
            style={` 
                left: ${iBar.x - 120}px;
                width: ${120}px;
                color: ${iBar.color};
                margin-top: ${54 + (barIndex + 1) * 86}px; `}>{iBar.kind}</span
        >
        <div
            class="bar bar-{iBar.kind}"
            style={`
          left: ${iBar.x}px;
          width: ${iBar.width}px;
          background: ${iBar.background || iBar.color};
          margin-top: ${54 + (barIndex + 1) * 86}px;
       
        `}
        />
    {/each}

    <!-- Lines -->
    {#each iMarks as mark, markIndex}
        <span
            class="mark-label"
            style={` 
            left: ${mark.x}px;
            color: ${mark.color};
            margin-top: ${16 + (markIndex + 1) * 16}px; `}>--{mark.name}</span
        >

        <div
            class="mark-div"
            style={`
      left: ${mark.x}px;
      border-left: 2px dashed ${mark.color};
 
    `}
        />
    {/each}
</div>

<style>
    .timeline {
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        position: relative;
        overflow-x: auto;
    }
    .axys {
        position: relative;
        height: 36px;
        display: flex;
        flex-shrink: 0;
        align-items: center;
    }

    .axy {
        font-size: 11px;
        color: var(--color-level-4);
        display: flex;
        flex-shrink: 0;
        position: absolute;
        padding: 8px 0;
        transition: left 0.4s ease-in-out;
    }
    .axy-bars {
        position: relative;
        width: 100%;
        height: 100%;
        display: flex;
    }

    .axy-bar {
        height: 100%;
        border-left: 1px solid var(--color-level-2);
        position: absolute;
        transition: left 0.3s ease-in-out;
    }

    .bars {
        position: relative;
        height: 36px;
    }
    .bar {
        position: absolute;
        height: 36px;
        border-radius: 4px;
        display: flex;
        justify-content: center;
        align-items: center;
        color: var(--color-level-6);
        transition: all 0.4s ease-in-out;
    }

    .label {
        position: absolute;
        font-size: 14px;
        transition: left 0.3s ease-in-out;
    }

    .kind {
        height: 36px;
        display: flex;
        justify-content: flex-end;
        align-items: center;
        padding: 16px;
        font-weight: 600;
        text-transform: capitalize;
    }
    .mark-label {
        position: absolute;
        font-size: 12px;
        display: flex;
        width: 100%;
        align-items: center;
        transition: all 0.4s ease-in-out;
    }

    .mark-div {
        position: absolute;
        height: 100%;
        width: 1px;
        z-index: 200;
        display: flex;
        justify-content: center;
        align-items: center;

        transition: all 0.4s ease-in-out;
    }
</style>
